import type { DayType, Meal, FoodInMeal, TemplateSnapshot } from '@/types/nutrition';

/**
 * NOTE: This file is kept for backward compatibility with existing database records
 * that still use the snapshot format. Once all records are migrated to template_data,
 * this file can be removed.
 *
 * Migration status: In progress
 * - New records: Use template_data only (editor format)
 * - Old records: May still have template_snapshot, need conversion
 * - Removal target: After all old records migrated (TBD)
 *
 * Database Snapshot Format:
 * - meal.entries[] (flat: kcal, protein_g, carbs_g, fat_g)
 * - meal.label
 *
 * Editor Format:
 * - meal.foods[] (nested: foodItem, nutrition)
 * - meal.name
 */

/**
 * Converts database snapshot format to editor format
 * Snapshot (entries[], kcal, protein_g) → Editor (foods[], nutrition.calories, nutrition.protein)
 *
 * Used by GlobalStateProvider to load old assignments that still have template_snapshot.
 */
export function snapshotToEditorFormat(snapshot: TemplateSnapshot | { days: any[] } | null | undefined): DayType[] {
  if (!snapshot || !snapshot.days || !Array.isArray(snapshot.days)) {
    return [];
  }

  return snapshot.days.map((day: any, idx: number) => {
    const meals: Meal[] = (day.meals || []).map((meal: any) => {
      const foods: FoodInMeal[] = (meal.entries || []).map((entry: any) => {
        // Calculate nutrition for the actual portion size
        // entry.kcal is per-100g, so scale by (portion_grams / 100) to get per-unit value
        const portionGrams = entry.portion_grams || 100;
        const quantity = entry.quantity || 1;
        const portionFactor = portionGrams / 100;

        const perPortionCalories = (entry.kcal || 0) * portionFactor;
        const perPortionProtein = (entry.protein_g || 0) * portionFactor;
        const perPortionCarbs = (entry.carbs_g || 0) * portionFactor;
        const perPortionFat = (entry.fat_g || 0) * portionFactor;

        // nutrition is the TOTAL for all units (per-portion * quantity)
        const totalCalories = perPortionCalories * quantity;
        const totalProtein = perPortionProtein * quantity;
        const totalCarbs = perPortionCarbs * quantity;
        const totalFat = perPortionFat * quantity;

        const result = {
          id: entry.food_id,
          foodItem: {
            id: entry.food_id,
            name: entry.name,
            calories: entry.kcal || 0, // Keep as per-100g
            protein: entry.protein_g || 0,
            carbs: entry.carbs_g || 0,
            fat: entry.fat_g || 0,
            // Use serving_size if available, otherwise fall back to portion_label
            serving_size: entry.serving_size || entry.portion_label || 'serving',
            category: 'custom',
            brand: entry.brand || null,
            defaultPortionGrams: entry.portion_grams || 100,
          },
          quantity: quantity,
          unit: entry.portion_label || 'serving',
          nutrition: {
            // TOTAL nutrition for all units (not per-unit!)
            calories: totalCalories,
            protein: totalProtein,
            carbs: totalCarbs,
            fat: totalFat,
          },
          // Restore baseNutrition - this is the immutable per-100g base values
          baseNutrition: {
            calories: entry.kcal || 0,
            protein: entry.protein_g || 0,
            carbs: entry.carbs_g || 0,
            fat: entry.fat_g || 0,
          },
          portion_id: entry.portion_id,
          portion_label: entry.portion_label,
          portion_grams: entry.portion_grams,
          total_kcal: totalCalories,
          total_protein_g: totalProtein,
          total_carbs_g: totalCarbs,
          total_fat_g: totalFat,
        };

        return result;
      });

      const totalNutrition = {
        calories: (meal.entries || []).reduce((sum: number, e: any) => sum + e.kcal * (e.quantity || 1), 0),
        protein: (meal.entries || []).reduce((sum: number, e: any) => sum + e.protein_g * (e.quantity || 1), 0),
        carbs: (meal.entries || []).reduce((sum: number, e: any) => sum + e.carbs_g * (e.quantity || 1), 0),
        fat: (meal.entries || []).reduce((sum: number, e: any) => sum + e.fat_g * (e.quantity || 1), 0),
      };

      return {
        id: `meal-${Math.random()}`,
        name: meal.label || meal.type || 'Meal',
        type: meal.type || 'custom',
        foods,
        totalNutrition,
      } as Meal;
    });

    return {
      id: `day-${idx}`,
      name: day.label || `Day ${idx + 1}`,
      meals,
    } as DayType;
  });
}

/**
 * Converts editor format to database snapshot format
 * Editor (foods[], nutrition.calories) → Snapshot (entries[], kcal, protein_g)
 */
export function editorToSnapshotFormat(dayTypes: DayType[]): TemplateSnapshot {
  return {
    days: (dayTypes || []).map((day: any) => {
      const label = day?.name || day?.dayName || day?.dayType || 'Day';
      return {
        label,
        meals: (day?.meals || []).map((meal: any) => {
          const mealType = meal?.type || meal?.id || 'custom';
          return {
            label: meal?.name || meal?.type || 'Meal',
            type: mealType,
            entries: (meal?.foods || []).map((f: any) => {
              // Use baseNutrition (immutable) if available, otherwise use current nutrition
              const baseNutr = f.baseNutrition || f.foodItem || f.nutrition || {};

              // For the per-unit base values (what we'll use to recalculate)
              const base_kcal_per_100g = Number(baseNutr.calories ?? 0);
              const base_protein_per_100g = Number(baseNutr.protein ?? 0);
              const base_carbs_per_100g = Number(baseNutr.carbs ?? 0);
              const base_fat_per_100g = Number(baseNutr.fat ?? 0);

              const portionGrams = Number(f.portion_grams ?? f.foodItem?.defaultPortionGrams ?? 100);
              const qty = Number(f.quantity ?? 1);

              const entry = {
                food_id: String(f.foodItem?.id ?? f.food_id ?? ''),
                name: String(f.foodItem?.name ?? f.name ?? 'Unknown Food'),
                brand: f.foodItem?.brand ?? f.brand ?? null,
                portion_id: f.portion_id ?? null,
                portion_label: String(f.portion_label ?? f.unit ?? f.foodItem?.serving_size ?? 'serving'),
                // Save the ORIGINAL serving_size from foodItem (e.g., "5oz (custom)")
                serving_size: String(f.foodItem?.serving_size ?? 'serving'),
                portion_grams: portionGrams,
                quantity: qty,
                total_grams: portionGrams * qty,
                // Store base nutrition per 100g so it can be recalculated correctly
                kcal: base_kcal_per_100g,
                protein_g: base_protein_per_100g,
                carbs_g: base_carbs_per_100g,
                fat_g: base_fat_per_100g,
              };

              return entry;
            }),
          };
        }),
      };
    }),
  };
}
